import Divider from "../../Divider";
import NumField from "../../NumField";
import {
  CapsuleTabContentBody,
  CapsuleTabContentHeader,
  ContentRow,
} from "../CapsuleComponents";
import { addDays } from "date-fns/fp";
import { subDays } from "date-fns";
import { IFormState, ServiceTypeConfiguration } from "../../../semshared/utils/lister";
import ServiceTypeSelection, { HugBase } from "../../ServiceTypeSelection";
import Icon from "../../Icon";
import DaySwitch from "../../DaySwitch";
import { LinkButton } from "../../LinkButton";
import { useState } from "react";
import { EPricing_ProductLookupCode } from "../../../semshared/pricelist/quickprice";
import { useTranslation } from "react-i18next";
import TooltipEnhanced from '../../TooltipEnhanced';
import {
  ServiceTypeContainer,
  ServiceTypeDescription,
  AdvancedContainer,
  PersonCount,
  AddonButton,
  AddonContainer,
  PrevDayDinnerContainer,
  IconContainer,
  DinnerInfo,
  PersonCountContainer,
} from "./ServiceTypeSection.styled";

type ServiceTypeKind = ServiceTypeConfiguration['type'];

interface ServiceTypeSectionProps {
  formState: IFormState;
  requestFormStateUpdate: (newState: IFormState) => void;
  meetingRoomsDay: any;
  setMeetingRoomsDay: any;
  advancedMode: boolean;
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
  onAddonAdded?: () => void;
}

/**
 * Replaces all entries of a package type with a single count (avoids duplicates from rapid typing).
 */
const upsertServiceTypeGuests = (
  serviceTypes: ServiceTypeConfiguration[],
  type: ServiceTypeKind,
  guestsCount: number,
): ServiceTypeConfiguration[] => [
  ...serviceTypes.filter((st) => st.type !== type),
  { type, guestsCount },
];

/**
 * Catering/service-type section for package and addon assignment.
 * @param props - Day context, mode flags, and state update callbacks.
 * @returns Service type editor in simple or advanced mode.
 */
const ServiceTypeSection = ({
  formState,
  requestFormStateUpdate,
  advancedMode,
  meetingRoomsDay,
  setMeetingRoomsDay,
  setDrawerShow,
  setPopupShow,
  onAddonAdded,
}: ServiceTypeSectionProps) => {
  const { t } = useTranslation();
  // Derive from formState directly — a lagged copy caused duplicate packages when typing multi-digit counts.
  const currentDay = formState.days.find((day) => day.day === meetingRoomsDay);
  const [isPrevDay, setIsPrevDay] = useState(false);

  const getInconsistencyDetails = (serviceLookupCode: string): { selectedDays: number; totalDays: number } | null => {
    const regularDays = formState.days.filter((day) => day.day >= 0);

    if (regularDays.length <= 1) return null;

    const daysWithService = regularDays.filter((day) =>
      day.addons?.some((addon) => addon.lookupCode === serviceLookupCode && addon.count > 0)
    );

    if (daysWithService.length > 0 && daysWithService.length < regularDays.length) {
      return {
        selectedDays: daysWithService.length,
        totalDays: regularDays.length
      };
    }

    return null;
  };

  const checkInconsistentService = (serviceLookupCode: string): boolean => {
    return getInconsistencyDetails(serviceLookupCode) !== null;
  };

  const hasInconsistentWelcomeCoffee = checkInconsistentService(EPricing_ProductLookupCode.ADDON_COFFEE);
  const hasInconsistentDinner = checkInconsistentService(EPricing_ProductLookupCode.ADDON_DINNER);

  const _guestsValue = (type: ServiceTypeKind) => {
    const match = currentDay?.serviceTypes.find((st) => st.type === type);
    return match?.guestsCount ?? 0;
  };

  const _renderContent = (type: ServiceTypeKind) => {
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: advancedMode ? "row" : "column",
            justifyContent: "space-between",
            margin: "10px 5px",
            gap: 10,
          }}
        >
          <div style={advancedMode ? {} : { marginBottom: 20 }}>
            {type === "SMALL" && <Icon name="serviceTypeSmall" />}
            {type === "MEDIUM" && <Icon name="serviceTypeMedium" />}
            {type === "REGULAR" && <Icon name="serviceTypeRegular" />}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ width: "100%", textAlign: "left", fontSize: "14px" }}>
              {type === "SMALL" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  <div style={{ marginBottom: 5 }}>{t('widget:serviceTypes.packageSmall')}</div>
                  <div>✓ 1x {t('widget:serviceTypes.coffeeBreak')}</div>
                </div>
              )}
              {type === "MEDIUM" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  <div style={{ marginBottom: 5 }}>{t('widget:serviceTypes.packageHalfDay')}</div>
                  <div>✓ 1x {t('widget:serviceTypes.coffeeBreak')}</div>
                  <div>✓ 1x {t('widget:serviceTypes.lunch')}</div>
                </div>
              )}
              {type === "REGULAR" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 5 }}
                >
                  <div style={{ marginBottom: 5 }}>{t('widget:serviceTypes.packageFullDay')}</div>
                  <div>✓ 2x {t('widget:serviceTypes.coffeeBreak')}</div>
                  <div>✓ 1x {t('widget:serviceTypes.lunch')}</div>
                </div>
              )}
            </div>
            {advancedMode && (
              <NumField
                value={_guestsValue(type)}
                onChange={(value) => {
                  if (value === 0) {
                    requestFormStateUpdate({
                      ...formState,
                      days: formState.days.map((day) => ({
                        ...day,
                        serviceTypes:
                          day.day === meetingRoomsDay
                            ? day.serviceTypes.filter((st) => st.type !== type)
                            : day.serviceTypes,
                        // Entkoppeln wenn REGULAR entfernt wird
                        ...(type === 'REGULAR' && day.day === meetingRoomsDay ? { regularGuestsSynced: false } : {}),
                      })),
                    });
                    return;
                  }

                  // REGULAR is uncoupled once count diverges from total guests for that day.
                  const shouldUnsync = type === 'REGULAR' && value !== currentDay?.totalGuests;
                  requestFormStateUpdate({
                    ...formState,
                    days: formState.days.map((day) =>
                      day.day === meetingRoomsDay
                        ? {
                          ...day,
                          serviceTypes: upsertServiceTypeGuests(day.serviceTypes, type, value),
                          ...(shouldUnsync ? { regularGuestsSynced: false } : {}),
                        }
                        : { ...day },
                    ),
                  });
                }}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  const _renderType = (type: any) => {
    return (
      <ServiceTypeSelection
        fullWidth={advancedMode}
        selected={
          formState.days[0].serviceTypes.filter((st) => st.type === type)
            .length === 1
        }
        onClick={() => {
          if (
            formState.days[0].serviceTypes.filter((st) => st.type === type)
              .length === 1
          ) {
            // Entfernen des Service Types
            requestFormStateUpdate({
              ...formState,
              days: formState.days.map((day) => ({
                ...day,
                serviceTypes: day.serviceTypes.filter((st) => st.type !== type),
                // Entkoppeln wenn REGULAR entfernt wird
                ...(type === 'REGULAR' ? { regularGuestsSynced: false } : {}),
              })),
            });
          } else {
            // Simple mode enforces exactly one package type, so selecting one replaces previous selections.
            requestFormStateUpdate({
              ...formState,
              days: formState.days.map((day) => ({
                ...day,
                serviceTypes: [{ type, guestsCount: day.totalGuests }],
                // Synchronisierung aktivieren wenn REGULAR ausgewählt wird (da guestsCount = totalGuests)
                ...(type === 'REGULAR' ? { regularGuestsSynced: true } : {}),
              })),
            });
          }
        }}
      >
        {_renderContent(type)}{" "}
      </ServiceTypeSelection>
    );
  };

  /** Clears all standard packages — same effect as deselecting the active package. */
  const _renderNoPackage = () => {
    const isSelected = formState.days[0].serviceTypes.length === 0;
    return (
      <ServiceTypeSelection
        selected={isSelected}
        onClick={() => {
          requestFormStateUpdate({
            ...formState,
            days: formState.days.map((day) => ({
              ...day,
              serviceTypes: [],
              regularGuestsSynced: false,
            })),
          });
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "space-between",
            margin: "10px 5px",
            gap: 10,
          }}
        >
          <div style={{ marginBottom: 20 }}>
            <Icon name="serviceTypeNone" />
          </div>
          <div style={{ width: "100%", textAlign: "left", fontSize: "14px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ marginBottom: 5 }}>{t('widget:serviceTypes.packageNone')}</div>
              <div>{t('widget:serviceTypes.packageNoneDescription')}</div>
            </div>
          </div>
        </div>
      </ServiceTypeSelection>
    );
  };

  const _renderTypeAdvanced = (type: any) => {
    return (
      <HugBase selected={false} fullWidth>
        {_renderContent(type)}{" "}
      </HugBase>
    );
  };

  const _renderAddonComponent = (
    lookupCode: "ADDON_COFFEE" | "ADDON_DINNER",
    label: string,
  ) => {
    const isSelected =
      formState.days[meetingRoomsDay].addons.filter(
        (a) => a.lookupCode === lookupCode,
      ).length > 0;

    // Determine which inconsistency check to use
    const inconsistencyDetails = getInconsistencyDetails(lookupCode);
    const hasInconsistency = inconsistencyDetails !== null;

    return (
      <>
        <AddonButton
          isSelected={isSelected}
          onClick={() => {
            if (
              formState.days[meetingRoomsDay].addons.filter(
                (a) => a.lookupCode === lookupCode,
              ).length === 0
            ) {
              requestFormStateUpdate({
                ...formState,
                days: formState.days.map((day) =>
                  day.day === meetingRoomsDay
                    ? {
                      ...day,
                      addons: [...day.addons, { lookupCode, count: 1 }],
                    }
                    : day,
                ),
              });
              onAddonAdded?.();
            }
            if (
              formState.days[meetingRoomsDay].addons.filter(
                (a) => a.lookupCode === lookupCode,
              ).length > 0
            ) {
              requestFormStateUpdate({
                ...formState,
                days: formState.days.map((day) =>
                  day.day === meetingRoomsDay
                    ? {
                      ...day,
                      addons: day.addons.filter(
                        (a) => a.lookupCode !== lookupCode,
                      ),
                    }
                    : day,
                ),
              });
            }
          }}
        >
          {isSelected ? "" : "+"} {label} {isSelected ? t('widget:serviceTypes.added') : ""}
        </AddonButton>
        {isSelected && (
          <AddonContainer>
            <div>
              <span>
                {t('widget:serviceTypes.enterPersonCount')}{" "}
                <strong>{label}</strong> {t('widget:serviceTypes.in')}{" "}
                {hasInconsistency && inconsistencyDetails && (
                  <TooltipEnhanced
                    content={t('widget:addons.inconsistent_service_warning_with_count', {
                      selectedDays: inconsistencyDetails.selectedDays,
                      totalDays: inconsistencyDetails.totalDays
                    })}
                    position="right"
                  >
                    <span style={{ transform: 'translateY(2px)', display: 'inline-flex' }}>
                      <Icon name="warning" />
                    </span>
                  </TooltipEnhanced>
                )}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
                <NumField
                  value={
                    formState.days[meetingRoomsDay].addons.filter(
                      (a) => a.lookupCode === lookupCode,
                    )[0].count
                  }
                  onChange={(value) => {
                    if (value === 0) {
                      requestFormStateUpdate({
                        ...formState,
                        days: formState.days.map((day) =>
                          day.day === meetingRoomsDay
                            ? {
                              ...day,
                              addons: day.addons.filter(
                                (a) => a.lookupCode !== lookupCode,
                              ),
                            }
                            : day,
                        ),
                      });
                    } else if (value > 0) {
                      requestFormStateUpdate({
                        ...formState,
                        days: formState.days.map((day) =>
                          day.day === meetingRoomsDay
                            ? {
                              ...day,
                              addons: day.addons.map((a) =>
                                a.lookupCode === lookupCode
                                  ? { ...a, count: value }
                                  : a,
                              ),
                            }
                            : day,
                        ),
                      });
                    }
                  }}
                />
              </div>
            </div>
          </AddonContainer>
        )}
      </>
    );
  };

  const _renderPrevDayDinnerAddon = () => {
    const lookupCode = "ADDON_DINNER";
    return (
      <HugBase selected={false} fullWidth>
        <PrevDayDinnerContainer>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <IconContainer>
              <Icon name="forkKnife" />
            </IconContainer>
            <DinnerInfo>
              <strong>{t('widget:serviceTypes.dinner')}</strong>
              <span>{t('widget:serviceTypes.dinnerTime')}</span>
            </DinnerInfo>
          </div>
          <PersonCountContainer>
            <span>{t('widget:serviceTypes.personCount')}</span>
            <NumField
              value={
                formState.prevdayAddons.find((a) => a.lookupCode === lookupCode)
                  ?.count ?? 0
              }
              onChange={(value) => {
                const hasValue = formState.prevdayAddons.some(
                  (item) => item.lookupCode === lookupCode,
                );
                const prevdayAddons = hasValue
                  ? formState.prevdayAddons.map((addon) =>
                    addon.lookupCode === lookupCode
                      ? { ...addon, count: value }
                      : addon,
                  )
                  : [...formState.prevdayAddons, { lookupCode, count: value }];
                requestFormStateUpdate({
                  ...formState,
                  prevdayAddons,
                });
              }}
            />
          </PersonCountContainer>
        </PrevDayDinnerContainer>
      </HugBase>
    );
  };

  const simple = () => {
    return (
      <>
        <CapsuleTabContentBody>
          <ServiceTypeContainer>
            {["SMALL", "MEDIUM", "REGULAR"].map((t) => (
              <div key={t}>{_renderType(t)}</div>
            ))}
            <div key="none">{_renderNoPackage()}</div>
          </ServiceTypeContainer>
          <ServiceTypeDescription>
          </ServiceTypeDescription>
          <Divider />
          <LinkButton
            onClick={() => {
              setDrawerShow("servicetypes");
              setPopupShow(null);
            }}
          >
            ⇾ Details
          </LinkButton>
        </CapsuleTabContentBody>
      </>
    );
  };

  const advanced = () => {
    let selectedDate = addDays(meetingRoomsDay, formState.start);
    if (isPrevDay) selectedDate = subDays(selectedDate, 1);
    const assignedToStandard = currentDay?.serviceTypes.reduce(
      (acc, st) => acc + st.guestsCount,
      0,
    ) || 0;
    const totalGuests = currentDay?.totalGuests || 0;
    const withoutStandard = Math.max(0, totalGuests - assignedToStandard);
    return (
      <>
        <CapsuleTabContentHeader>
          <ContentRow>
            <DaySwitch
              value={meetingRoomsDay}
              min={-1}
              max={formState.days.length - 1}
              onChange={(v) => {
                setMeetingRoomsDay(v > -1 ? v : 0);
                setIsPrevDay(v === -1);
              }}
              date={selectedDate}
            />
          </ContentRow>
        </CapsuleTabContentHeader>
        <AdvancedContainer>
          {isPrevDay ? (
            _renderPrevDayDinnerAddon()
          ) : (
            <>
              {["SMALL", "MEDIUM", "REGULAR"].map((t) => (
                <div key={t}>
                  {advancedMode ? _renderTypeAdvanced(t) : _renderType(t)}
                </div>
              ))}

              {advancedMode && (
                <PersonCount isOverLimit={assignedToStandard > totalGuests}>
                  <span>
                    {t('widget:serviceTypes.assignedToStandardPackages', {
                      assigned: assignedToStandard,
                      total: totalGuests,
                    })}
                  </span>
                  <span>
                    {t('widget:serviceTypes.participantsWithoutStandardPackage', {
                      count: withoutStandard,
                    })}
                  </span>
                </PersonCount>
              )}
              {_renderAddonComponent(EPricing_ProductLookupCode.ADDON_COFFEE, t('widget:addons.coffee'))}
              {_renderAddonComponent(EPricing_ProductLookupCode.ADDON_DINNER, t('widget:addons.dinner'))}
            </>
          )}
        </AdvancedContainer>
      </>
    );
  };

  return <>{advancedMode ? advanced() : simple()}</>;
};

export default ServiceTypeSection;
